import type { LangChainMessage } from "@assistant-ui/react-langgraph";

export const AGENT_SELECTED_TEXT_METADATA_KEY = "agent_selected_text";
export const AGENT_UPLOADED_FILES_METADATA_KEY = "agent_uploaded_files";
export const AGENT_QUESTION_RESPONSES_METADATA_KEY = "agent_question_responses";

/**
 * Persisted text for a human turn whose real payload lives in message metadata.
 * The user-message renderer suppresses this reserved value when one of the
 * supported metadata keys is present.
 */
export const METADATA_ONLY_MESSAGE_TEXT = "[Context attached]";

const SUPPORTED_METADATA_KEYS = [
  AGENT_SELECTED_TEXT_METADATA_KEY,
  AGENT_UPLOADED_FILES_METADATA_KEY,
  AGENT_QUESTION_RESPONSES_METADATA_KEY
] as const;

const asRecord = (value: unknown): Record<string, unknown> =>
  value !== null && typeof value === "object"
    ? (value as Record<string, unknown>)
    : {};

const hasSupportedMetadata = (value: unknown): boolean => {
  const metadata = asRecord(value);
  return SUPPORTED_METADATA_KEYS.some((key) =>
    Object.prototype.hasOwnProperty.call(metadata, key)
  );
};

const isBlankContent = (value: unknown): boolean => {
  if (value == null) return true;
  if (typeof value === "string") return !value.trim();
  if (Array.isArray(value)) return value.every(isBlankContent);

  const part = asRecord(value);
  return part.type === "text" ? isBlankContent(part.text) : false;
};

export const createMetadataOnlyMessageContent = () => [
  { type: "text" as const, text: METADATA_ONLY_MESSAGE_TEXT }
];

/**
 * Ensure metadata-backed human messages remain valid provider history.
 * This runs after upload and per-feature message preparation so all supported
 * metadata keys are present before the blank-content check.
 * @param messages - Outgoing LangChain messages for the next run.
 * @returns The original array when valid, otherwise a copy with placeholders.
 */
export const ensureMetadataOnlyMessagesHaveContent = (
  messages: LangChainMessage[]
): LangChainMessage[] => {
  let changed = false;
  const prepared = messages.map((message) => {
    if (message.type !== "human" || !isBlankContent(message.content)) {
      return message;
    }

    const metadata = asRecord(message.additional_kwargs).metadata;
    if (!hasSupportedMetadata(metadata)) return message;

    changed = true;
    return { ...message, content: METADATA_ONLY_MESSAGE_TEXT };
  });

  return changed ? prepared : messages;
};

/**
 * Whether a user message contains only the reserved, non-visible placeholder.
 *
 * The sentinel text is minted by this module and never typed by a user, so it
 * alone decides. It deliberately does NOT also require the metadata that
 * prompted the placeholder. The payload does travel with the message (the
 * repo's `patches/` change to `useLangGraphRuntime` sends `metadata.custom`
 * as `additional_kwargs.metadata`), but the optimistic user message is
 * rendered before that round trip converts it back, and requiring both made
 * the raw "[Context attached]" text visible in that window.
 * @param parts - Renderable assistant-ui message parts.
 * @returns True when the placeholder should be suppressed in the UI.
 */
export const isHiddenMetadataOnlyPlaceholder = (
  parts: readonly unknown[]
): boolean => {
  const text = parts
    .map((part) => asRecord(part))
    .filter((part) => part.type === "text")
    .map((part) => (typeof part.text === "string" ? part.text : ""))
    .join("")
    .trim();

  return text === METADATA_ONLY_MESSAGE_TEXT;
};
